import { z } from 'zod'
import { EmailSchema, OkResponseSchema, ProviderIdSchema, UuidSchema } from '../primitives'
import { route } from '../route-def'
import { ReasonCategorySchema } from '../domain/reasons'
import { DEVICE_LOCATION_MODES, GPS_REFUSAL_REASONS } from '../domain/location-policy'
import { AttemptSummarySchema } from './attempts'
import { SuspectHintSchema } from './location'

/**
 * Registration steps S1–S3 (spec "Registration flow", U1–U3, R6, R11, R12, R23; AC20, AC21, AC24, AC27): step 1,
 * the email code (code box or the emailed confirm link, POST only), the city and the device location (location v2), the "continue"
 * link, the provider switch and the passwordless erasure link. Task 11 appended the attempt fields to the status.
 */

/** Same values as the API's `members.stage` / `members.status` (apps/api checks they stay equal at compile time). */
export const REGISTRATION_STAGES = [
  'email_pending',
  'email_verified',
  'location_set',
  'biometric_pending',
  /** Face first (2026-10-30 spec §2.2): the face decision is stored and the identity document is open. */
  'document_pending',
  'decided',
] as const
export const REGISTRATION_STATUSES = [
  'approved',
  'review',
  'rejected',
  'blocked_duplicate',
  'retry_allowed',
  'erased',
] as const
export const RegistrationStageSchema = z.enum(REGISTRATION_STAGES)
export const RegistrationStatusValueSchema = z.enum(REGISTRATION_STATUSES)

/**
 * Where the web app goes next (S2 code, S3 location, S4 face, S4-wait, S6 status, or the sign-in page).
 * Face first (2026-10-30 spec §2.1, §4.4): `document` follows the face step (stage `document_pending`) while the
 * requirement applies. Passwordless (2026-11-03 spec §2, P6): no `password` step; a decided `approved` / `review`
 * registration lands on `status`, which claims the voter session (`memberSessionClaim`); `login` (continue link of a
 * decided registration) sends the voter to the sign-in email.
 */
export const REGISTRATION_NEXT_STEPS = [
  'verify_email',
  'location',
  'face',
  'wait',
  'status',
  'login',
  'document',
] as const
export const RegistrationNextSchema = z.enum(REGISTRATION_NEXT_STEPS)
export type RegistrationNext = z.infer<typeof RegistrationNextSchema>

export const Step1BodySchema = z.object({
  provider: ProviderIdSchema,
  name: z.string().trim().min(1).max(100),
  email: EmailSchema,
  acceptPrivacy: z.literal(true),
})
export type Step1Body = z.infer<typeof Step1BodySchema>

/** The same answer for a free, an existing and a blocklisted email (U3, R11). */
export const Step1ResponseSchema = z.object({
  next: z.literal('verify_email'),
  resendAfterSeconds: z.number().int(),
})
export type Step1Response = z.infer<typeof Step1ResponseSchema>

export const EmailCodeSchema = z
  .string()
  .trim()
  .regex(/^\d{6}$/)

export const VerifyCodeBodySchema = z.object({ code: EmailCodeSchema })
export const ConfirmLinkBodySchema = z.object({ codeId: UuidSchema, code: EmailCodeSchema })
export const CodeVerifiedResponseSchema = z.object({ next: z.literal('location') })
export const ResendResponseSchema = z.object({ resendAfterSeconds: z.number().int() })

export const ContinueBodySchema = z.object({ token: z.string().min(10).max(300) })
export const ContinueResponseSchema = z.object({ next: RegistrationNextSchema })
export type ContinueResponse = z.infer<typeof ContinueResponseSchema>

export const CITY_NOTE_MAX = 200
export const CityBodySchema = z.object({
  placeId: z.number().int().positive(),
  note: z.string().trim().max(CITY_NOTE_MAX).optional(),
})
/** One sample of the S3 location watch (diagnostics only, location v2 audit 6). */
export const GpsSampleSchema = z.object({
  accuracyM: z.number().positive().max(10_000_000),
  /** Age of the sample when it reached the page (Date.now() − position.timestamp), ms. */
  ageMs: z.number().min(-60_000).max(86_400_000),
  hasAltitude: z.boolean(),
  hasSpeed: z.boolean(),
})
export type GpsSample = z.infer<typeof GpsSampleSchema>
export const GPS_SAMPLES_MAX = 20

/**
 * The device fix of S3 (location v2 "Device location collection"): the best sample of a watch, with its age measured
 * on the device (no clock skew) and the watch diagnostics. `placeId` is the place it is measured against (default:
 * the saved place).
 */
export const GpsBodySchema = z.object({
  placeId: z.number().int().positive().optional(),
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  accuracyM: z.number().positive().max(10_000_000),
  ageMs: z.number().min(-60_000).max(86_400_000).optional(),
  timeZone: z.string().trim().max(64).optional(),
  firstAccuracyM: z.number().positive().max(10_000_000).optional(),
  elapsedMs: z.number().int().min(0).max(600_000).optional(),
  samples: z.array(GpsSampleSchema).max(GPS_SAMPLES_MAX).optional(),
  /** Page-side spoof hints (e.g. `non_native_geolocation`); shown to the admin, never a reason. */
  suspect: z.array(SuspectHintSchema).max(5).optional(),
})
export type GpsBody = z.infer<typeof GpsBodySchema>

export const GpsRefusedBodySchema = z.object({
  reason: z.enum(GPS_REFUSAL_REASONS),
  timeZone: z.string().trim().max(64).optional(),
})

/** What S3 needs to know about the device location (location v2 "Server enforcement"). */
export const RegistrationLocationSchema = z.object({
  mode: z.enum(DEVICE_LOCATION_MODES),
  /** Accuracy (m) above which S3 shows the approximate-location hint. */
  hintAccuracyM: z.number(),
  /** The place of a fix received within the last 10 minutes (Continue works for it), else null. */
  fixPlaceId: z.number().int().nullable(),
  /** A refusal is recorded (and is the latest location answer). */
  refused: z.boolean(),
  /** The face step would refuse to start (409 device_location_required): back to S3. */
  missing: z.boolean(),
})
export type RegistrationLocation = z.infer<typeof RegistrationLocationSchema>
export const SwitchProviderBodySchema = z.object({ provider: ProviderIdSchema })

export const RegistrationCitySchema = z.object({
  id: z.number().int(),
  name: z.string(),
  admin1: z.string().nullable(),
  country: z.string(),
  note: z.string().nullable(),
})

export const OtherProviderSchema = z.object({ id: z.string(), displayName: z.string(), joinPath: z.string() })

/** Where the identity-document step stands (identity-document spec §12.1; api/documents.ts has the details). */
export const DOCUMENT_STEP_STATES = [
  'not_needed',
  'needed',
  'in_progress',
  'checking',
  'confirm_name',
  'retry',
  'evidence_ready',
  'exempt',
  'manual',
] as const

/** The registration status' document block: whether the requirement applies and where the step stands. */
export const RegistrationDocumentSchema = z.object({
  applies: z.boolean(),
  state: z.enum(DOCUMENT_STEP_STATES),
})
export type RegistrationDocument = z.infer<typeof RegistrationDocumentSchema>

export const RegistrationStatusSchema = z.object({
  stage: RegistrationStageSchema,
  status: RegistrationStatusValueSchema.nullable(),
  provider: z.string(),
  providerDisplayName: z.string(),
  name: z.string().nullable(),
  emailMasked: z.string().nullable(),
  city: RegistrationCitySchema.nullable(),
  /** Plain-language reason categories of a review (age, place, face, document). */
  reasonCategories: z.array(ReasonCategorySchema),
  /**
   * Passwordless (2026-11-03 spec §2, P5): decided `approved` or `review`. The status page then calls
   * `POST /api/member/session/claim` by itself (no button) and goes to the voter page.
   */
  claimable: z.boolean(),
  /** True while the provider may still be changed (R6: no attempt other than start_failed). */
  canSwitchProvider: z.boolean(),
  /** The other active providers (only meaningful while canSwitchProvider). */
  otherProviders: z.array(OtherProviderSchema),
  /* Task 11: the face step. */
  /** The latest attempt (any status), or null before the first start. */
  attempt: AttemptSummarySchema.nullable().default(null),
  /** Counted attempts left (BIOMETRIC_MAX_ATTEMPTS + extra attempts − counted, never below 0). */
  attemptsLeft: z.number().int().min(0).default(0),
  /** A new face check can start now (stage, attempts left, provider active). */
  canStart: z.boolean().default(false),
  /* Location v2: the device-location mode and state. */
  location: RegistrationLocationSchema,
  /* Identity document (spec §4.1, §12.1): the 6-step flow while the requirement applies. */
  document: RegistrationDocumentSchema,
})
export type RegistrationStatus = z.infer<typeof RegistrationStatusSchema>

/**
 * Passwordless (2026-11-03 spec §2, rulings P5, P11/M3): `POST /api/member/session/claim`, no body, CSRF-checked.
 * - 200 `{ next: 'dashboard' }`: the registration cookie (its `rep` equal to `registration_epoch`) of a decided
 *   `approved` / `review` registration, not erased. `registration_epoch` + 1 (the registration cookie and every
 *   continue link die), the voter session cookie `ipc_member` is set (replacing another voter's in this browser) and
 *   the registration cookie is cleared. Also 200, and nothing changes, when the registration cookie is gone or dead
 *   but the browser holds a valid voter session of the same voter (idempotent: a second call, another tab).
 * - 401 `invalid_registration`: neither (no valid registration cookie and no voter session of that voter; e.g. a
 *   second device with the same registration cookie after the first claim). The web offers the sign-in email.
 * - 409 `invalid_state_transition`: the registration is valid but not claimable (any other stage or status:
 *   `retry_allowed`, `rejected`, `blocked_duplicate`, still in progress); the registration pages stay.
 */
export const SessionClaimResponseSchema = z.object({ next: z.literal('dashboard') })
export type SessionClaimResponse = z.infer<typeof SessionClaimResponseSchema>

export const registrationRoutes = {
  registrationStart: route({
    method: 'POST',
    path: '/api/member/registration',
    auth: 'public',
    body: Step1BodySchema,
    response: Step1ResponseSchema,
  }),
  registrationStatus: route({
    method: 'GET',
    path: '/api/member/registration',
    auth: 'registration',
    response: RegistrationStatusSchema,
  }),
  registrationCode: route({
    method: 'POST',
    path: '/api/member/registration/code',
    auth: 'registration',
    body: VerifyCodeBodySchema,
    response: CodeVerifiedResponseSchema,
  }),
  registrationCodeResend: route({
    method: 'POST',
    path: '/api/member/registration/code/resend',
    auth: 'registration',
    response: ResendResponseSchema,
  }),
  registrationConfirm: route({
    method: 'POST',
    path: '/api/member/registration/confirm',
    auth: 'public',
    body: ConfirmLinkBodySchema,
    response: CodeVerifiedResponseSchema,
  }),
  registrationContinue: route({
    method: 'POST',
    path: '/api/member/registration/continue',
    auth: 'public',
    body: ContinueBodySchema,
    response: ContinueResponseSchema,
  }),
  registrationCity: route({
    method: 'POST',
    path: '/api/member/registration/city',
    auth: 'registration',
    body: CityBodySchema,
    response: OkResponseSchema,
  }),
  registrationGps: route({
    method: 'POST',
    path: '/api/member/registration/gps',
    auth: 'registration',
    body: GpsBodySchema,
    response: OkResponseSchema,
  }),
  registrationGpsRefused: route({
    method: 'POST',
    path: '/api/member/registration/gps/refused',
    auth: 'registration',
    body: GpsRefusedBodySchema,
    response: OkResponseSchema,
  }),
  registrationProvider: route({
    method: 'POST',
    path: '/api/member/registration/provider',
    auth: 'registration',
    body: SwitchProviderBodySchema,
    response: OkResponseSchema,
  }),
  registrationErasureLink: route({
    method: 'POST',
    path: '/api/member/registration/erasure-link',
    auth: 'registration',
    response: OkResponseSchema,
  }),
  /** Passwordless (2026-11-03 spec §2, P5): the registration session becomes the voter session. */
  memberSessionClaim: route({
    method: 'POST',
    path: '/api/member/session/claim',
    auth: 'member_or_registration',
    response: SessionClaimResponseSchema,
  }),
  /** R110: "Email me a link to continue" (help sheet); the continue email is never sent by itself. */
  registrationContinueLink: route({
    method: 'POST',
    path: '/api/member/registration/continue-link',
    auth: 'registration',
    response: OkResponseSchema,
  }),
}
